import { useState } from "react";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  ROTULO_DO_PEDIDO,
  useAprovacoesDoChamado,
  useDecidirAprovacao,
  type PedidoDeAprovacao,
} from "../../nucleo/aprovacoes";
import { Aviso, Botao, CampoLongo, Cartao, Chip } from "../ui";

function tomDoPedido(estado: PedidoDeAprovacao["estado"]): "ok" | "atencao" | "erro" | "neutro" | "aguardando" {
  if (estado === "aprovada") return "ok";
  if (estado === "recusada") return "erro";
  if (estado === "pendente") return "aguardando";
  return "neutro";
}

function Pedido({ ticketId, pedido }: { ticketId: string; pedido: PedidoDeAprovacao }) {
  const decidir = useDecidirAprovacao(ticketId);
  const [comentario, setComentario] = useState("");
  const [erroDoComentario, setErroDoComentario] = useState<string | undefined>();

  function responder(aprovar: boolean) {
    if (!aprovar && comentario.trim().length < 3) {
      setErroDoComentario("Para recusar, explique o motivo.");
      return;
    }
    setErroDoComentario(undefined);
    decidir.mutate({ pedidoId: pedido.id, aprovar, comentario });
  }

  return (
    <Cartao
      titulo={pedido.regra_nome}
      acessorio={<Chip estado={tomDoPedido(pedido.estado)}>{ROTULO_DO_PEDIDO[pedido.estado]}</Chip>}
      className="mb-3"
      data-testid={`pedido-de-aprovacao-${pedido.estado}`}
    >
      <p className="cartao__texto">
        {pedido.aprovados} de {pedido.minimo_aprovacoes}{" "}
        {pedido.minimo_aprovacoes === 1 ? "aprovação necessária" : "aprovações necessárias"}
        {pedido.estado === "pendente" && pedido.pausa_o_sla ? " · o prazo do atendimento está pausado" : ""}
      </p>
      <ul className="lista-de-itens mt-2">
        {pedido.aprovadores.map((aprovador) => (
          <li key={aprovador.usuario_id}>
            <strong>{aprovador.nome}</strong>{" "}
            {aprovador.decisao === "aprovada" ? (
              <Chip estado="ok" semPonto>Aprovou</Chip>
            ) : aprovador.decisao === "recusada" ? (
              <Chip estado="erro" semPonto>Recusou</Chip>
            ) : (
              <Chip estado="neutro" semPonto>Ainda não decidiu</Chip>
            )}
            {aprovador.comentario ? <span className="nota-rodape"> · “{aprovador.comentario}”</span> : null}
          </li>
        ))}
      </ul>
      {pedido.pode_decidir ? (
        <div className="mt-3" data-testid="decidir-aprovacao">
          <CampoLongo
            rotulo="Comentário"
            ajuda="Obrigatório para recusar. Recusar cancela o chamado."
            value={comentario}
            onChange={(evento) => setComentario(evento.target.value)}
            erro={erroDoComentario}
            rows={2}
            maxLength={2000}
          />
          <div className="flex flex-wrap gap-3 mt-2">
            <Botao variante="verde" disabled={decidir.isPending} onClick={() => responder(true)}>
              Aprovar
            </Botao>
            <Botao variante="fantasma" disabled={decidir.isPending} onClick={() => responder(false)}>
              Recusar
            </Botao>
          </div>
        </div>
      ) : null}
      {decidir.isError ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(decidir.error)}</Aviso> : null}
    </Cartao>
  );
}

/** Pedidos de aprovação do chamado: quem precisa aprovar, o que já decidiram e o botão de quem pode. */
export function AprovacoesDoChamado({ ticketId }: { ticketId: string }) {
  const pedidos = useAprovacoesDoChamado(ticketId);
  if (pedidos.isPending) return null;
  if (pedidos.isError) return <Aviso tom="erro" className="mb-4">{mensagemDoConsole(pedidos.error)}</Aviso>;
  if (pedidos.data.length === 0) return null;
  const aguardando = pedidos.data.some((pedido) => pedido.estado === "pendente");
  return (
    <section className="mb-4" aria-labelledby={`aprovacoes-${ticketId}`} data-testid="aprovacoes-do-chamado">
      <h2 className="titulo-secao" id={`aprovacoes-${ticketId}`}>Aprovação</h2>
      {aguardando ? (
        <Aviso tom="atencao" className="mb-3">
          Este chamado aguarda aprovação e só segue depois da decisão de quem aprova.
        </Aviso>
      ) : null}
      {pedidos.data.map((pedido) => (
        <Pedido key={pedido.id} ticketId={ticketId} pedido={pedido} />
      ))}
    </section>
  );
}
